import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { planoService } from '../../services/planoService';
import { Plano, AcessoChoiceLabels, AcessoChoiceNumericToStringKey } from '../../types/plano';

export const ListaPlanos: React.FC = () => {
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPlanos, setTotalPlanos] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  useEffect(() => {
    const fetchPlanos = async (page: number) => {
      setLoading(true);
      try {
        const response = await planoService.listar(page, limit);
        setPlanos(response.results);
        setTotalPlanos(response.count);
        setTotalPages(Math.ceil(response.count / limit));
      } catch (err) {
        console.error('Erro ao carregar planos:', err);
        setError('Erro ao carregar a lista de planos');
      } finally {
        setLoading(false);
      }
    };

    fetchPlanos(currentPage);
  }, [currentPage]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este plano?')) {
      return;
    }

    try {
      await planoService.excluir(id);
      setPlanos(planos.filter(plano => plano.id !== id));
    } catch (err) {
      console.error('Erro ao excluir plano:', err);
      setError('Erro ao excluir o plano');
    }
  };

  const formatarValor = (valor: string) => {
    const numValor = parseFloat(valor);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValor);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#32bef0]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{error}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[#29306a]">Planos</h1>
          <p className="mt-2 text-sm text-[#64748b]">
            Lista de todos os planos cadastrados no sistema
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <Link
            to="/planos/novo"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-[#32bef0] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#2ba8d8] focus:outline-none focus:ring-2 focus:ring-[#32bef0] focus:ring-offset-2 sm:w-auto"
          >
            Adicionar plano
          </Link>
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[#29306a] sm:pl-6"
                    >
                      Nome
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-[#29306a]"
                    >
                      Descrição
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-[#29306a]"
                    >
                      Valor
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-[#29306a]"
                    >
                      Máx. Membros
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-[#29306a]"
                    >
                      Qtd. Cidades
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-[#29306a]"
                    >
                      Qtd. Segmentos
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-[#29306a]"
                    >
                      Acessos
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    >
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {planos.map((plano) => (
                    <tr key={plano.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-[#29306a] sm:pl-6">
                        {plano.nome}
                      </td>
                      <td className="px-3 py-4 text-sm text-[#64748b]">
                        {plano.descricao}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-[#64748b]">
                        {formatarValor(plano.valor)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-[#64748b]">
                        {plano.max_membros}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-[#64748b]">
                        {plano.quantidade_cidade}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-[#64748b]">
                        {plano.quantidade_segmento}
                      </td>
                      <td className="px-3 py-4 text-sm text-[#64748b]">
                        <div className="flex flex-wrap gap-1">
                          {plano.acessos.map(acesso => {
                            const numericAcesso = typeof acesso === 'string' ? parseInt(acesso) : acesso;
                            const stringKey = typeof numericAcesso === 'number' && !isNaN(numericAcesso)
                              ? AcessoChoiceNumericToStringKey[numericAcesso] || acesso
                              : acesso;
                            return (
                              <span key={acesso} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                {AcessoChoiceLabels[stringKey] || stringKey}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <Link
                          to={`/planos/${plano.id}/editar`}
                          className="text-[#32bef0] hover:text-[#2ba8d8] mr-4"
                        >
                          Editar
                        </Link>
                        <button
                          onClick={() => handleDelete(plano.id)}
                          className="text-[#f55eeb] hover:text-[#e54dd8]"
                        >
                          Excluir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Próxima
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando{' '}
                      <span className="font-medium">{(currentPage - 1) * limit + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(currentPage * limit, totalPlanos)}</span> de{' '}
                      <span className="font-medium">{totalPlanos}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav
                      className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                      >
                        <span className="sr-only">Anterior</span>
                        &lt;
                      </button>
                      <span
                        aria-current="page"
                        className="relative z-10 inline-flex items-center bg-[#32bef0] px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#32bef0]"
                      >
                        {currentPage}
                      </span>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                      >
                        <span className="sr-only">Próxima</span>
                        &gt;
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
 